import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';

const formatCurrency = (value) => {
  if (value == null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const CartwheelTips = ({ startDate: initialStartDate, endDate: initialEndDate, firebaseUser }) => {
  // Local date state (initialized from props)
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [fiscalPeriods, setFiscalPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [loadingPeriods, setLoadingPeriods] = useState(true);

  const [groupBy, setGroupBy] = useState('partner');
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ partners: [], stores: [] });
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Expandable rows state - tracks which rows are expanded and their child data
  const [expandedRows, setExpandedRows] = useState({}); // { rowKey: { loading, data } }

  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Sync with parent dates when they change
  useEffect(() => {
    if (initialStartDate && initialEndDate) {
      setStartDate(initialStartDate);
      setEndDate(initialEndDate);
    }
  }, [initialStartDate, initialEndDate]);

  // Fetch fiscal periods on mount
  useEffect(() => {
    const fetchFiscalPeriods = async () => {
      if (!firebaseUser) return;

      try {
        setLoadingPeriods(true);
        const token = await firebaseUser.getIdToken(true);

        const [periodsResponse, currentResponse] = await Promise.all([
          axios.get('/hd-api/gl-export/fiscal-periods', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get('/hd-api/gl-export/current-period', {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setFiscalPeriods(periodsResponse.data.periods || []);

        // Set selected period if dates match current period
        if (currentResponse.data && startDate && endDate) {
          const current = currentResponse.data;
          const [startYear, startMonth, startDay] = current.startDate.split('T')[0].split('-');
          const [endYear, endMonth, endDay] = current.endDate.split('T')[0].split('-');
          const currentStart = new Date(startYear, startMonth - 1, startDay);
          const currentEnd = new Date(endYear, endMonth - 1, endDay);

          if (
            startDate.toDateString() === currentStart.toDateString() &&
            endDate.toDateString() === currentEnd.toDateString()
          ) {
            setSelectedPeriod(current.label);
          }
        }
      } catch (err) {
        console.error('Error fetching fiscal periods:', err);
      } finally {
        setLoadingPeriods(false);
      }
    };

    fetchFiscalPeriods();
  }, [firebaseUser]);

  // Handle fiscal period selection
  const handlePeriodChange = (event) => {
    const periodLabel = event.target.value;
    setSelectedPeriod(periodLabel);

    if (periodLabel === '') {
      return; // Custom - don't change dates
    }

    const period = fiscalPeriods.find((p) => p.label === periodLabel);
    if (period) {
      const [startYear, startMonth, startDay] = period.startDate.split('T')[0].split('-');
      const [endYear, endMonth, endDay] = period.endDate.split('T')[0].split('-');
      setStartDate(new Date(startYear, startMonth - 1, startDay));
      setEndDate(new Date(endYear, endMonth - 1, endDay));
    }
  };

  // When dates are manually changed, set period to Custom
  const handleStartDateChange = (date) => {
    setStartDate(date);
    setSelectedPeriod('');
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setSelectedPeriod('');
  };

  // Fetch filter options
  const fetchFilters = useCallback(async () => {
    if (!startDate || !endDate || !firebaseUser) return;

    try {
      setFiltersLoading(true);
      const token = await firebaseUser.getIdToken(true);
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      const response = await axios.get(`/hd-api/gl-export/cartwheel-tips/filters?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setFilters({
        partners: response.data.partners || [],
        stores: response.data.stores || [],
      });
    } catch (err) {
      console.error('Error fetching filters:', err);
    } finally {
      setFiltersLoading(false);
    }
  }, [startDate, endDate, firebaseUser]);

  // Fetch data based on current filters and grouping
  const fetchData = useCallback(async () => {
    if (!startDate || !endDate || !firebaseUser) return;

    try {
      setLoading(true);
      setError(null);
      setExpandedRows({}); // Clear expanded rows when reloading
      const token = await firebaseUser.getIdToken(true);

      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        groupBy,
      });

      selectedPartners.forEach((p) => params.append('deliveryPartner', p));
      selectedStores.forEach((s) => params.append('store', s));

      const response = await axios.get(`/hd-api/gl-export/cartwheel-tips?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(response.data.data || []);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error fetching Cartwheel tips:', err);
      setError('Failed to load Cartwheel tips data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, firebaseUser, groupBy, selectedPartners, selectedStores]);

  // Fetch data when filters/grouping change (but only after initial load)
  useEffect(() => {
    if (hasLoaded) {
      fetchData();
    }
    // Reset sort when groupBy changes
    setSortColumn(null);
    setSortDirection('asc');
  }, [groupBy, selectedPartners, selectedStores]);

  // Handle Load button click
  const handleLoad = async () => {
    await fetchFilters();
    await fetchData();
  };

  // Handle row expansion toggle
  const handleRowClick = async (row, rowKey) => {
    // Only allow expansion for 'day' and 'partner' groupings
    if (groupBy !== 'day' && groupBy !== 'partner') return;

    // If already expanded, collapse it
    if (expandedRows[rowKey]) {
      setExpandedRows((prev) => {
        const newState = { ...prev };
        delete newState[rowKey];
        return newState;
      });
      return;
    }

    // Start loading child data
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: { loading: true, data: [] },
    }));

    try {
      const token = await firebaseUser.getIdToken(true);
      let params;

      if (groupBy === 'day') {
        // Expanding a day row - fetch by partner for that specific day
        const clickedDate = row.BUSINESS_DATE;
        const [month, day, year] = clickedDate.split('/');
        const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

        params = new URLSearchParams({
          startDate: `${dateStr}T00:00:00.000Z`,
          endDate: `${dateStr}T23:59:59.999Z`,
          groupBy: 'partner',
        });
      } else if (groupBy === 'partner') {
        // Expanding a partner row - fetch by day for that partner
        params = new URLSearchParams({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          groupBy: 'day',
        });
        params.append('deliveryPartner', row.DELIVERY_PARTNER);
      }

      // Apply store filters
      selectedStores.forEach((s) => params.append('store', s));

      const response = await axios.get(`/hd-api/gl-export/cartwheel-tips?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setExpandedRows((prev) => ({
        ...prev,
        [rowKey]: { loading: false, data: response.data.data || [] },
      }));
    } catch (err) {
      console.error('Error fetching child data:', err);
      setExpandedRows((prev) => ({
        ...prev,
        [rowKey]: { loading: false, data: [] },
      }));
    }
  };

  // Handle sort column click
  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      // Toggle direction or clear sort
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Numeric columns
      if (['ORDER_COUNT', 'CUSTOMER_DELIVERY_FEE', 'TIP', 'SUBTOTAL', 'TOTAL'].includes(sortColumn)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // Date column (MM/DD/YYYY format)
      if (sortColumn === 'BUSINESS_DATE') {
        const [aMonth, aDay, aYear] = String(aVal).split('/');
        const [bMonth, bDay, bYear] = String(bVal).split('/');
        aVal = new Date(aYear, aMonth - 1, aDay).getTime();
        bVal = new Date(bYear, bMonth - 1, bDay).getTime();
      }

      // String comparison for other columns
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortColumn, sortDirection]);

  // Calculate totals
  const totals = useMemo(() => {
    return data.reduce(
      (acc, row) => ({
        orderCount: acc.orderCount + (row.ORDER_COUNT || 0),
        customerDeliveryFee: acc.customerDeliveryFee + (row.CUSTOMER_DELIVERY_FEE || 0),
        tip: acc.tip + (row.TIP || 0),
        subtotal: acc.subtotal + (row.SUBTOTAL || 0),
        total: acc.total + (row.TOTAL || 0),
      }),
      { orderCount: 0, customerDeliveryFee: 0, tip: 0, subtotal: 0, total: 0 }
    );
  }, [data]);

  // Handle export
  const handleExport = async (exportType) => {
    setExportMenuAnchor(null);
    if (!startDate || !endDate || !firebaseUser) return;

    try {
      setExporting(true);
      const token = await firebaseUser.getIdToken(true);

      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      selectedPartners.forEach((p) => params.append('deliveryPartner', p));
      selectedStores.forEach((s) => params.append('store', s));

      let endpoint;
      let filename;
      const dateRange = `${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}`;

      if (exportType === 'summarized') {
        // Export the current grouped/summarized view
        params.append('groupBy', groupBy);
        params.append('exportType', 'summarized');
        endpoint = `/hd-api/gl-export/cartwheel-tips/export?${params.toString()}`;
        filename = `cartwheel-tips-${groupBy}-${dateRange}.xlsx`;
      } else {
        // Export granular/detailed data
        params.append('exportType', 'granular');
        endpoint = `/hd-api/gl-export/cartwheel-tips/export?${params.toString()}`;
        filename = `cartwheel-tips-details-${dateRange}.xlsx`;
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 300000,
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Error exporting Cartwheel tips:', err);
      setError('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Get row key for expansion tracking
  const getRowKey = (row) => {
    if (groupBy === 'day') return row.BUSINESS_DATE;
    if (groupBy === 'partner') return row.DELIVERY_PARTNER;
    if (groupBy === 'store') return row.STORE;
    return null;
  };

  // Check if row is expandable
  const isExpandable = groupBy === 'day' || groupBy === 'partner';

  // Get columns based on groupBy
  const getColumns = () => {
    const baseColumns = [
      { id: 'ORDER_COUNT', label: 'Orders', align: 'right' },
      { id: 'CUSTOMER_DELIVERY_FEE', label: 'Customer Delivery Fee', align: 'right', format: formatCurrency },
      { id: 'TIP', label: 'Tip', align: 'right', format: formatCurrency },
      { id: 'SUBTOTAL', label: 'Subtotal', align: 'right', format: formatCurrency },
      { id: 'TOTAL', label: 'Total', align: 'right', format: formatCurrency },
    ];

    switch (groupBy) {
      case 'partner':
        return [{ id: 'DELIVERY_PARTNER', label: 'Delivery Partner', align: 'left' }, ...baseColumns];
      case 'day':
        return [{ id: 'BUSINESS_DATE', label: 'Date', align: 'left' }, ...baseColumns];
      case 'store':
        return [{ id: 'STORE', label: 'Store', align: 'left' }, ...baseColumns];
      case 'summary':
        return [
          { id: 'ORDER_COUNT', label: 'Total Orders', align: 'right' },
          { id: 'CUSTOMER_DELIVERY_FEE', label: 'Customer Delivery Fee', align: 'right', format: formatCurrency },
          { id: 'TIP', label: 'Tip', align: 'right', format: formatCurrency },
          { id: 'SUBTOTAL', label: 'Subtotal', align: 'right', format: formatCurrency },
          { id: 'TOTAL', label: 'Total', align: 'right', format: formatCurrency },
        ];
      default:
        return [];
    }
  };

  // Get child columns (opposite of parent grouping)
  const getChildColumns = () => {
    if (groupBy === 'day') {
      return [
        { id: 'DELIVERY_PARTNER', label: 'Delivery Partner', align: 'left' },
        { id: 'ORDER_COUNT', label: 'Orders', align: 'right' },
        { id: 'CUSTOMER_DELIVERY_FEE', label: 'Customer Delivery Fee', align: 'right', format: formatCurrency },
        { id: 'TIP', label: 'Tip', align: 'right', format: formatCurrency },
        { id: 'SUBTOTAL', label: 'Subtotal', align: 'right', format: formatCurrency },
        { id: 'TOTAL', label: 'Total', align: 'right', format: formatCurrency },
      ];
    } else if (groupBy === 'partner') {
      return [
        { id: 'BUSINESS_DATE', label: 'Date', align: 'left' },
        { id: 'ORDER_COUNT', label: 'Orders', align: 'right' },
        { id: 'CUSTOMER_DELIVERY_FEE', label: 'Customer Delivery Fee', align: 'right', format: formatCurrency },
        { id: 'TIP', label: 'Tip', align: 'right', format: formatCurrency },
        { id: 'SUBTOTAL', label: 'Subtotal', align: 'right', format: formatCurrency },
        { id: 'TOTAL', label: 'Total', align: 'right', format: formatCurrency },
      ];
    }
    return [];
  };

  const columns = getColumns();
  const childColumns = getChildColumns();

  // Render a single row with optional expansion
  const renderRow = (row, idx) => {
    const rowKey = getRowKey(row);
    const isExpanded = !!expandedRows[rowKey];
    const childData = expandedRows[rowKey]?.data || [];
    const childLoading = expandedRows[rowKey]?.loading || false;

    return (
      <React.Fragment key={idx}>
        <TableRow
          hover
          onClick={() => isExpandable && handleRowClick(row, rowKey)}
          sx={{
            cursor: isExpandable ? 'pointer' : 'default',
            '&:hover': isExpandable ? { backgroundColor: 'action.selected' } : {},
          }}
        >
          {/* Expand icon for expandable rows */}
          {isExpandable && (
            <TableCell sx={{ width: 40, p: 0.5 }}>
              <IconButton size="small">
                {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
            </TableCell>
          )}
          {columns.map((col) => (
            <TableCell key={col.id} align={col.align}>
              {col.format ? col.format(row[col.id]) : row[col.id]}
            </TableCell>
          ))}
        </TableRow>

        {/* Child rows */}
        {isExpandable && (
          <TableRow>
            <TableCell
              colSpan={columns.length + 1}
              sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}
            >
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 6, pr: 2, py: 1, backgroundColor: 'grey.50' }}>
                  {childLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : childData.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No data available
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {childColumns.map((col) => (
                            <TableCell
                              key={col.id}
                              align={col.align}
                              sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}
                            >
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {childData.map((childRow, childIdx) => (
                          <TableRow key={childIdx}>
                            {childColumns.map((col) => (
                              <TableCell
                                key={col.id}
                                align={col.align}
                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                              >
                                {col.format ? col.format(childRow[col.id]) : childRow[col.id]}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2, mb: 0 }}>
          {error}
        </Alert>
      )}

      {/* Toolbar Section */}
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: (theme) => theme.palette.grey[50],
        }}
      >
        {/* Date Controls Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Fiscal Period</InputLabel>
            <Select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              label="Fiscal Period"
              disabled={loadingPeriods}
            >
              <MenuItem value="">
                <em>Custom</em>
              </MenuItem>
              {fiscalPeriods.map((period) => (
                <MenuItem key={period.label} value={period.label}>
                  {period.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatePicker
            selected={startDate}
            onChange={handleStartDateChange}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText="Start Date"
            customInput={
              <TextField size="small" label="Start Date" sx={{ width: 140 }} autoComplete="off" />
            }
          />
          <DatePicker
            selected={endDate}
            onChange={handleEndDateChange}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            placeholderText="End Date"
            customInput={
              <TextField size="small" label="End Date" sx={{ width: 140 }} autoComplete="off" />
            }
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleLoad}
            disabled={!startDate || !endDate || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
          >
            Load
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            disabled={exporting || data.length === 0}
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            endIcon={<KeyboardArrowDownIcon />}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleExport('summarized')}>
              <ListItemIcon>
                <TableChartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Summarized (Current View)</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExport('granular')}>
              <ListItemIcon>
                <ListAltIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Granular (All Details)</ListItemText>
            </MenuItem>
          </Menu>
        </Box>

        {/* Filters Row */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 250 }}
            options={filters.partners}
            value={selectedPartners}
            onChange={(e, val) => setSelectedPartners(val)}
            loading={filtersLoading}
            renderInput={(params) => (
              <TextField {...params} label="Delivery Partner" placeholder="All Partners" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
              ))
            }
          />

          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 250 }}
            options={filters.stores}
            value={selectedStores}
            onChange={(e, val) => setSelectedStores(val)}
            loading={filtersLoading}
            renderInput={(params) => (
              <TextField {...params} label="Store" placeholder="All Stores" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
              ))
            }
          />

          <Box sx={{ flexGrow: 1 }} />

          {/* Group By Toggle */}
          <ToggleButtonGroup
            value={groupBy}
            exclusive
            onChange={(e, val) => val && setGroupBy(val)}
            size="small"
            sx={{
              backgroundColor: 'white',
            }}
          >
            <ToggleButton value="partner">
              <LocalShippingIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Partner
            </ToggleButton>
            <ToggleButton value="day">
              <CalendarTodayIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Day
            </ToggleButton>
            <ToggleButton value="store">
              <StorefrontIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Store
            </ToggleButton>
            <ToggleButton value="summary">
              <SummarizeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Summary
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Data Table */}
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {/* Empty cell for expand icon */}
              {isExpandable && <TableCell sx={{ width: 40 }} />}
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => handleSort(col.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                    {col.label}
                    {sortColumn === col.id && (
                      sortDirection === 'asc'
                        ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                        : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + (isExpandable ? 1 : 0)} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (isExpandable ? 1 : 0)} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {hasLoaded ? 'No data available for the selected date range' : 'Click Load to fetch data'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {sortedData.map((row, idx) => renderRow(row, idx))}
                {/* Totals Row */}
                {groupBy !== 'summary' && data.length > 0 && (
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    {isExpandable && <TableCell />}
                    <TableCell sx={{ fontWeight: 600 }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {totals.orderCount.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(totals.customerDeliveryFee)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(totals.tip)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(totals.subtotal)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(totals.total)}
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CartwheelTips;
